import { useEffect, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { api, fmtErr } from "../../../lib/api";
import { SupplierFormBody } from "../MarketplaceAdmin";
import { toast } from "sonner";

// Self-serve company profile for a logged-in marketplace supplier.
// Lives under My profile → Company details. Renders the editable form
// inline (always editable), mirroring "My personal details".
export default function SupplierCompanyDetails() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taxonomy, setTaxonomy] = useState({ categories: [], specialties: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/marketplace/my-supplier"); setProfile(data); }
    catch (e) { if (e?.response?.status !== 404) toast.error(fmtErr(e)); setProfile(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([api.get("/marketplace/categories"), api.get("/marketplace/specialties")])
      .then(([c, s]) => setTaxonomy({ categories: c.data, specialties: s.data }))
      .catch(() => {});
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-zinc-500 p-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading company details…</div>;

  if (!profile) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500" data-testid="company-details-empty">
        Your account isn't linked to a marketplace supplier yet. Ask the Seavanto admin to link your login to your company profile.
      </div>
    );
  }

  if (profile.readonly) {
    const rows = [
      ["Company", profile.company_name],
      ["Email", profile.email],
      ["Phone", profile.phone],
      ["Website", profile.website],
      ["City", profile.city],
      ["Country", profile.country],
      ["Categories", (profile.category_names || []).join(", ")],
      ["About", profile.description],
    ].filter(([, v]) => v);
    return (
      <div data-testid="company-details-readonly">
        <p className="text-sm text-zinc-500 mb-4">
          Your company's marketplace profile. Only a company admin can edit it — your own
          contact details live under <strong>Account preferences</strong>.
        </p>
        <div className="rounded-xl border border-zinc-200 bg-white divide-y divide-zinc-100">
          {rows.map(([k, v]) => (
            <div key={k} className="flex gap-4 px-4 py-3 text-sm">
              <div className="w-28 shrink-0 text-zinc-400">{k}</div>
              <div className="text-zinc-800 break-words min-w-0">{v}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="company-details-section">
      <p className="text-sm text-zinc-500 mb-4">
        Your public marketplace listing. Owners see this when searching for suppliers.
      </p>
      <SupplierFormBody
        inline
        selfServe
        editing={profile}
        categories={taxonomy.categories}
        specialties={taxonomy.specialties}
        onSaved={load}
        onCancel={load}
      />
    </div>
  );
}
